import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { TableColumnProps } from "../../typings/global";
import { PaginationProps } from "../../typings";
import { ROWS_PER_PAGE_OPTIONS } from "../../data/pagination";
import { UserActionMenu } from "./UserActionMenu";

interface UsersTableProps {
  columns: TableColumnProps[];
  data: any[];
  loading?: boolean;
  searchTerm?: string;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  pagination: PaginationProps;
  onPageChange: (e: unknown, page: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// ─── Skeleton Row ─────────────────────────────────────────────────────────────
const SkeletonRow: React.FC<{ colCount: number; hasActions: boolean }> = ({
  colCount,
  hasActions,
}) => (
  <TableRow>
    {Array.from({ length: colCount }).map((_, i) => (
      <TableCell key={i}>
        <Skeleton
          variant="rounded"
          animation="wave"
          height={18}
          sx={{
            borderRadius: "6px",
            bgcolor: "rgba(32, 99, 71, 0.07)",
            "&::after": {
              background:
                "linear-gradient(90deg, transparent, rgba(32,99,71,0.12), transparent)",
            },
          }}
          width={i === 0 ? "40%" : i === colCount - 1 ? "60%" : "75%"}
        />
      </TableCell>
    ))}
    {hasActions && (
      <TableCell align="center">
        <Box display="flex" justifyContent="center" gap={1}>
          <Skeleton
            variant="circular"
            animation="wave"
            width={30}
            height={30}
            sx={{ bgcolor: "rgba(32, 99, 71, 0.07)" }}
          />
          <Skeleton
            variant="circular"
            animation="wave"
            width={30}
            height={30}
            sx={{ bgcolor: "rgba(239, 68, 68, 0.07)" }}
          />
          <Skeleton
            variant="circular"
            animation="wave"
            width={30}
            height={30}
            sx={{ bgcolor: "rgba(32, 99, 71, 0.07)" }}
          />
        </Box>
      </TableCell>
    )}
  </TableRow>
);

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState: React.FC<{ searchTerm?: string }> = ({ searchTerm }) => {
  const hasSearch = Boolean(searchTerm?.trim());

  return (
    <TableRow>
      <TableCell colSpan={100} sx={{ border: "none", py: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            px: 3,
            gap: 2,
          }}
        >
          {/* Illustrated circle */}
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ECFDF3 0%, #D1FADF 100%)",
              border: "2px solid rgba(32, 99, 71, 0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: -6,
                borderRadius: "50%",
                border: "1px dashed rgba(32, 99, 71, 0.2)",
              },
            }}
          >
            {hasSearch ? (
              /* Magnifier + no-results cross */
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <circle cx="19" cy="19" r="11" stroke="#206347" strokeWidth="2.2" strokeLinecap="round" />
                <path d="M27 27L37 37" stroke="#206347" strokeWidth="2.2" strokeLinecap="round" />
                <line x1="15" y1="15" x2="23" y2="23" stroke="#F04438" strokeWidth="1.8" strokeLinecap="round" />
                <line x1="23" y1="15" x2="15" y2="23" stroke="#F04438" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            ) : (
              /* Person / user group icon */
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <circle cx="22" cy="16" r="7" fill="#D1FADF" stroke="#206347" strokeWidth="1.8" />
                <path
                  d="M8 36c0-7.732 6.268-14 14-14s14 6.268 14 14"
                  stroke="#339F71"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  fill="none"
                />
                <circle cx="34" cy="18" r="4.5" fill="#ECFDF3" stroke="#339F71" strokeWidth="1.4" />
                <path
                  d="M38 30c0-4.418-1.79-8-4-8"
                  stroke="#A6F4C5"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            )}
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#206347",
              fontSize: "1.1rem",
              letterSpacing: "-0.01em",
            }}
          >
            {hasSearch ? "No users found" : "No users yet"}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#667085",
              textAlign: "center",
              maxWidth: 340,
              lineHeight: 1.6,
              fontSize: "0.875rem",
            }}
          >
            {hasSearch ? (
              <>
                We couldn't find any users matching{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: "#344054",
                    backgroundColor: "#ECFDF3",
                    border: "1px solid #A6F4C5",
                    borderRadius: "4px",
                    px: 0.75,
                    py: 0.25,
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                  }}
                >
                  {searchTerm}
                </Box>
                . Try adjusting the name you're searching for.
              </>
            ) : (
              "Users created through the portal will appear here. Use the Create User button above to add one."
            )}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
};

// ─── Main Table ───────────────────────────────────────────────────────────────
const UsersTable: React.FC<UsersTableProps> = ({
  columns,
  data,
  loading = false,
  searchTerm = "",
  onEdit,
  onDelete,
  pagination,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();
  const hasActions = Boolean(onEdit || onDelete);

  const [orderBy, setOrderBy] = useState<keyof any>(columns[0].id);
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const handleSort =
    (property: keyof any) => (event: React.MouseEvent<unknown>) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
      if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, order, orderBy]);

  const skeletonRowCount = Math.min(pagination.page_size, 8);

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: 3,
        borderRadius: 2,
        transition: "opacity 0.25s ease",
        opacity: loading ? 0.85 : 1,
      }}
    >
      <TableContainer sx={{ maxHeight: 630 }}>
        <Table stickyHeader aria-label="users table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={handleSort(column.id)}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {column.label}
                    </Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
              {hasActions && (
                <TableCell align="center">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Actions
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              /* ── Skeleton shimmer rows ── */
              Array.from({ length: skeletonRowCount }).map((_, i) => (
                <SkeletonRow key={i} colCount={columns.length} hasActions={hasActions} />
              ))
            ) : sortedData.length === 0 ? (
              /* ── Beautiful empty state ── */
              <EmptyState searchTerm={searchTerm} />
            ) : (
              /* ── Real data rows ── */
              sortedData.map((row, index) => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={index}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.colors["white"]["400"],
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format ? column.format(value) : value}
                      </TableCell>
                    );
                  })}
                  {hasActions && (
                    <TableCell align="center">
                      {onEdit && (
                        <IconButton
                          aria-label="edit"
                          onClick={() => onEdit(row)}
                          size="small"
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton
                          aria-label="delete"
                          onClick={() => onDelete(row)}
                          size="small"
                          sx={{ color: theme.palette.error.main }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                      <UserActionMenu user={row} />
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={pagination.total_no_of_records}
        rowsPerPage={pagination.page_size}
        page={pagination.page_number - 1}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Paper>
  );
};

export default UsersTable;
